import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Link } from "wouter";
import { cx } from "./cx.js";
import { Icon } from "./Icon.jsx";

export type ButtonVariant = "primary" | "secondary" | "quiet" | "danger";

const VARIANT: Record<ButtonVariant, string> = {
  primary: "bg-brand text-white border border-brand hover:bg-brand-hover",
  secondary: "bg-surface text-ink border border-line-strong hover:bg-subtle",
  quiet: "bg-transparent text-brand border border-transparent hover:bg-brand-subtle",
  danger: "bg-alarm text-white border border-alarm hover:bg-alarm-hover",
};
const DISABLED = "cursor-not-allowed border border-line bg-subtle text-ink-muted";
/** On the shop floor even a disabled label meets AAA (7:1): muted ink on white, not on the grey fill. */
const DISABLED_FLOOR = "cursor-not-allowed border border-line bg-surface text-ink-muted";
const BASE =
  "inline-flex items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium no-underline focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-brand";

/**
 * Primary / secondary / quiet / danger. A button that cannot be pressed stays focusable (aria-disabled) and says why,
 * both in a tooltip and to a screen reader, rather than silently going grey. With `href` it is a link styled alike.
 */
export function Button({
  variant = "secondary",
  icon,
  loading,
  disabledReason,
  size = "office",
  children,
  className,
  onClick,
  href,
  type = "button",
  ...rest
}: {
  variant?: ButtonVariant;
  icon?: string;
  /** The action is running: the button stays in place and refuses a second press. */
  loading?: boolean;
  /** Why the button cannot be used right now; supplying one is the way to disable a button. */
  disabledReason?: string | undefined;
  /** "floor" sizes the target to the 60px glove rule; the default is the 36px office target. */
  size?: "office" | "floor";
  href?: string;
  children: ReactNode;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children">) {
  const off = !!disabledReason || !!loading || !!rest.disabled;
  const cls = cx(
    BASE,
    off ? (size === "floor" ? DISABLED_FLOOR : DISABLED) : VARIANT[variant],
    size === "floor" ? "min-h-[var(--touch-floor)] px-5" : "min-h-9",
    className,
  );
  const inner = (
    <>
      {loading ? <Icon name="progress_activity" size={18} /> : icon ? <Icon name={icon} size={18} /> : null}
      {children}
    </>
  );
  if (href && !off) {
    return (
      <Link href={href} className={cls}>
        {inner}
      </Link>
    );
  }
  const reasonId = disabledReason ? `${rest.id ?? "btn"}-why` : undefined;
  return (
    <>
      <button
        {...rest}
        type={type}
        className={cls}
        aria-disabled={off || undefined}
        aria-busy={loading || undefined}
        aria-describedby={reasonId}
        title={disabledReason ?? rest.title}
        onClick={off ? (e) => e.preventDefault() : onClick}
      >
        {inner}
      </button>
      {reasonId ? (
        <span id={reasonId} className="sr-only">
          {disabledReason}
        </span>
      ) : null}
    </>
  );
}
